import React, { useEffect, useMemo, useState } from 'react';
import { Button, Col, Form, Modal, Offcanvas, Row, Stack, Alert, Spinner } from 'react-bootstrap';
import { useIsMobile } from '../hooks/useIsMobile';

type StreamPlatform = 'YOUTUBE' | 'FACEBOOK' | 'TWITCH' | 'CUSTOM';

export type StreamingDestinationFormValue = {
  id?: string;
  platform: StreamPlatform;
  url: string;
  streamKey: string;
};

type StreamingDestinationModalProps = {
  show: boolean;
  mode: 'create' | 'edit';
  initialValue?: Partial<StreamingDestinationFormValue>; // pass destination when editing
  onClose: () => void;
  onSave: (value: StreamingDestinationFormValue) => Promise<void> | void;
  onUpdate: (id: string, value: StreamingDestinationFormValue) => Promise<void> | void;
  // Optional: allow forcing mobile/desktop from parent (useful for storybook/tests)
  forceVariant?: 'desktop' | 'mobile';
  isProcessing?: boolean; // show saving state without disabling the form (useful for parent-managed state)
  error?: string; // error message to display (useful for parent-managed state)
};

const platformOptions: { value: StreamPlatform; label: string; hint?: string }[] = [
  { value: 'YOUTUBE', label: 'YouTube', hint: 'rtmp://a.rtmp.youtube.com/live2' },
  {
    value: 'FACEBOOK',
    label: 'Facebook',
    hint: 'rtmp://live-api-s.facebook.com:80/rtmp',
  },
  { value: 'TWITCH', label: 'Twitch', hint: 'rtmp://live.twitch.tv/app' },
  { value: 'CUSTOM', label: 'Custom RTMP' },
];

export function StreamingDestinationModal({
  show,
  mode,
  initialValue,
  onClose,
  onSave,
  onUpdate,
  forceVariant,
  isProcessing,
  error,
}: StreamingDestinationModalProps) {
  const isMobileAuto = useIsMobile(992); // bootstrap lg breakpoint
  const isMobile = forceVariant ? forceVariant === 'mobile' : isMobileAuto;

  const defaults: StreamingDestinationFormValue = useMemo(
    () => ({
      id: initialValue?.id,
      platform: (initialValue?.platform as StreamPlatform) ?? 'YOUTUBE',
      url: initialValue?.url ?? '',
      streamKey: initialValue?.streamKey ?? '',
    }),
    [initialValue],
  );

  const [value, setValue] = useState<StreamingDestinationFormValue>(defaults);
  const [revealKey, setRevealKey] = useState(false);
  const [touched, setTouched] = useState(false);

  // Reset form when opening / changing destination
  useEffect(() => {
    if (!show) return;
    setValue(defaults);
    setRevealKey(false);
    setTouched(false);
  }, [show, defaults]);

  const title = mode === 'create' ? 'Add destination' : 'Edit destination';

  const urlHint = useMemo(() => {
    const opt = platformOptions.find(p => p.value === value.platform);
    return opt?.hint;
  }, [value.platform]);

  const validate = (v: StreamingDestinationFormValue) => {
    const issues: string[] = [];
    if (!v.platform) issues.push('Choose a platform.');
    if (!v.url.trim()) issues.push('RTMP URL is required.');
    if (!v.streamKey.trim()) issues.push('Stream key is required.');
    // light RTMP sanity check (non-blocking)
    if (v.url.trim() && !/^rtmps?:\/\//i.test(v.url.trim())) {
      issues.push('RTMP URL should start with rtmp:// or rtmps://');
    }
    return issues;
  };

  const issues = touched ? validate(value) : [];
  const canSave = issues.length === 0 && !isProcessing;

  const handleClose = () => {
    if (isProcessing) return;
    onClose();
  };

  const handleSave = () => {
    setTouched(true);
    const errs = validate(value);
    if (errs.length) return;
    onSave(value);
  };

  const handleUpdate = () => {
    setTouched(true);
    const errs = validate(value);
    if (errs.length) return;
    if (initialValue?.id) {
      onUpdate(initialValue.id, value);
    }
  };

  const onChange = <K extends keyof StreamingDestinationFormValue>(key: K, next: StreamingDestinationFormValue[K]) => {
    setValue(prev => ({ ...prev, [key]: next }));
  };

  const copy = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
    } catch {
      // ignore quietly
    }
  };

  const FormContent = (
    <>
      <div className={isMobile ? 'px-3 pt-3' : 'p-3'}>
        {error && (
          <Alert variant="danger" className="mb-3">
            {error}
          </Alert>
        )}

        <Form>
          <Row className="g-3">
            <Col xs={12}>
              <Form.Label className="fw-semibold">Platform</Form.Label>
              <Form.Select
                value={value.platform}
                onChange={e => {
                  const platform = e.target.value as StreamPlatform;
                  onChange('platform', platform);
                }}
              >
                {platformOptions.map(p => (
                  <option key={p.value} value={p.value}>
                    {p.label}
                  </option>
                ))}
              </Form.Select>
              {urlHint && (
                <Form.Text className="text-muted">
                  Typical RTMP URL: <span className="font-monospace">{urlHint}</span>
                </Form.Text>
              )}
            </Col>

            <Col xs={12}>
              <Form.Label className="fw-semibold">RTMP URL</Form.Label>
              <Form.Control
                placeholder="rtmp://..."
                value={value.url}
                onChange={e => onChange('url', e.target.value)}
                onBlur={() => setTouched(true)}
              />
            </Col>

            <Col xs={12}>
              <Form.Label className="fw-semibold">Stream key</Form.Label>
              <Form.Control
                type={revealKey ? 'text' : 'password'}
                placeholder="••••••••••••••"
                value={value.streamKey}
                onChange={e => onChange('streamKey', e.target.value)}
                onBlur={() => setTouched(true)}
                autoComplete="off"
              />

              <div className="mt-2 d-flex flex-wrap gap-2 align-items-center">
                <Form.Check
                  type="switch"
                  id="reveal-key"
                  label="Reveal key"
                  checked={revealKey}
                  onChange={e => setRevealKey(e.currentTarget.checked)}
                />
                <Button
                  variant="outline-secondary"
                  size="sm"
                  onClick={() => copy(value.streamKey)}
                  disabled={!value.streamKey.trim()}
                >
                  Copy key
                </Button>
              </div>

              <Form.Text className="text-muted">
                Keep this private. Anyone with the key can stream to this destination.
              </Form.Text>
            </Col>

            {touched && issues.length > 0 && (
              <Col xs={12}>
                <Alert variant="warning" className="mb-0">
                  <div className="fw-semibold mb-1">Please fix:</div>
                  <ul className="mb-0">
                    {issues.map(i => (
                      <li key={i}>{i}</li>
                    ))}
                  </ul>
                </Alert>
              </Col>
            )}
          </Row>
        </Form>
      </div>

      {/* Footer */}
      <div className={isMobile ? 'border-top bg-white p-3' : 'border-top bg-white p-3 rounded-bottom'}>
        <Stack direction="horizontal" gap={2} className="justify-content-end">
          <Button variant="outline-secondary" onClick={handleClose} disabled={isProcessing}>
            Cancel
          </Button>
          <Button variant="primary" onClick={mode === 'create' ? handleSave : handleUpdate} disabled={!canSave}>
            {isProcessing ? (
              <>
                <Spinner animation="border" size="sm" /> Saving…
              </>
            ) : mode === 'create' ? (
              'Add destination'
            ) : (
              'Save changes'
            )}
          </Button>
        </Stack>
      </div>
    </>
  );

  // Desktop: centered Modal
  if (!isMobile) {
    return (
      <Modal show={show} onHide={handleClose} centered backdrop="static" keyboard={!isProcessing}>
        <Modal.Header closeButton={!isProcessing}>
          <Modal.Title>{title}</Modal.Title>
        </Modal.Header>

        {/* We use custom body+footer to keep the same layout as mobile */}
        <div>{FormContent}</div>
      </Modal>
    );
  }

  // Mobile: bottom sheet using Offcanvas placement="bottom"
  return (
    <Offcanvas
      show={show}
      onHide={handleClose}
      placement="bottom"
      backdrop="static"
      scroll={false}
      style={{ height: 'auto' }}
    >
      <Offcanvas.Header closeButton={!isProcessing}>
        <Offcanvas.Title>{title}</Offcanvas.Title>
      </Offcanvas.Header>

      <div className="d-flex flex-column">
        <div className="flex-grow-1 overflow-auto">{FormContent}</div>
      </div>
    </Offcanvas>
  );
}
